<template>
  <FlotoAnchorLink :aria-label="$tc('add_solution')" @click="triggerAction">
    <MIcon name="puzzle-piece" class="action-menu-icon" />
    <span class="mx-1 action-menu-text">
      {{ $tc('add_solution') }}
    </span>
    <FlotoDrawerForm
      :open="showForm"
      width="60%"
      @submit="handleFormSubmit"
      @cancel="showForm = false"
    >
      <template v-slot:header>
        {{ $t('add_solution') }}
      </template>
      <MRow :gutter="0">
        <MCol>
          <div class="flex">
            <FlotoFormItem
              id="text-input"
              rules="required"
              class="w-full"
              :validation-label="$t('text')"
            >
              <FlotoRichEditor
                :key="renderCount"
                ref="editorRef"
                v-model="text"
                auto-focus
                :name="$t('text')"
                :rows="8"
                :affix-toolbar="false"
                :placeholder="$t('start_typing')"
                :max-height="500"
                class="flex-1 mb-1"
              />
            </FlotoFormItem>
          </div>
          <MCol class="mb-3 px-2">
            <FlotoAttachment v-model="attachments" only-list />
          </MCol>
          <MCol class="mb-3 px-2 flex">
            <FlotoAttachment
              v-model="attachments"
              :show-upload-list="false"
              @file-list-updated="fileList = $event"
            />
            <InsertResponseTemplate
              v-if="moduleName !== $constants.PROBLEM"
              ref="insertResponseTemplateDrawer"
              :resource="{}"
              :allow-suggested-response-template="false"
              @selected="handleInsertTemplate"
            >
              <template v-slot:trigger="{ showDrawer }">
                <MTooltip>
                  <template v-slot:trigger>
                    <MButton
                      variant="neutral-light"
                      class="mx-1"
                      :shadow="false"
                      shape="circle"
                      @click="showDrawer"
                    >
                      <MIcon name="window-restore" />
                    </MButton>
                  </template>
                  {{ $t('insert') }} {{ $t('from') }} {{ $tc('template') }}
                </MTooltip>
              </template>
            </InsertResponseTemplate>
            <InsertKnowledge
              ref="insertKnowledgeDrawer"
              :resource="{}"
              :hide-suggested-knowledge="true"
              @selected="handleInsertKnowledge"
            >
              <template v-slot:trigger="{ showDrawer }">
                <MTooltip>
                  <template v-slot:trigger>
                    <MButton
                      variant="neutral-light"
                      class="mx-1"
                      :shadow="false"
                      shape="circle"
                      @click="showDrawer"
                    >
                      <MIcon name="lightbulb-on" />
                    </MButton>
                  </template>
                  {{ $t('insert') }} {{ $t('knowledge') }}
                </MTooltip>
              </template>
            </InsertKnowledge>
          </MCol>
        </MCol>
      </MRow>
      <template v-slot:actions="{ hide, submit }">
        <MButton
          id="update-btn"
          class="mr-2"
          :loading="processing"
          @click="handleSolutionUpdate"
        >
          {{ $t('update') }}
        </MButton>
        <MButton id="cancel-btn" variant="default" @click="hide">
          {{ $t('cancel') }}
        </MButton>
      </template>
    </FlotoDrawerForm>
    <FlotoConfirmModal
      :open="showConfirmModal"
      no-icon-shadow
      @hide="showConfirmModal = false"
      @confirm="handleFormSubmit(getResolveStatusId)"
      @cancel="handleCancelBulkUpdateStatus"
    >
      <template v-slot:icon>
        <MIcon name="check" class="w-full text-secondary-green" size="3x" />
      </template>
      <template v-slot:message>
        {{ $t('ask_resolve_item', { item: $tc(moduleName) }) }}
      </template>
    </FlotoConfirmModal>
  </FlotoAnchorLink>
</template>

<script>
import {
  bulkUpdateRequestSolutionApi,
  bulkUpdateProblemSolutionApi,
  bulkUpdateApi,
} from '../../ticket-api'
import InsertResponseTemplate from '@src/components/conversation/insert-response-template.vue'
import InsertKnowledge from '@src/components/conversation/insert-knowledge.vue'
import { StatusComputed } from '@/src/state/modules/status'
import { PreferenceComputed } from '@/src/state/modules/preference'
import { errorToast } from '@motadata/ui'
export default {
  name: 'AddSolution',
  components: { InsertKnowledge, InsertResponseTemplate },
  props: {
    selectedIds: { type: Array, required: true },
    moduleName: { type: String, required: true },
  },
  data() {
    return {
      renderCount: 1,
      showForm: false,
      processing: false,
      conversationType: {},
      text: '',
      attachments: [],
      showConfirmModal: false,
    }
  },
  computed: {
    ...StatusComputed,
    ...PreferenceComputed,
    getResolveStatusId() {
      if (
        [this.$constants.REQUEST, this.$constants.PROBLEM].indexOf(
          this.moduleName
        ) >= 0 &&
        this[`${this.moduleName}FindStatus`]
      ) {
        return this[`${this.moduleName}FindStatus`]('resolved').id
      }
      return undefined
    },
  },
  methods: {
    triggerAction() {
      this.$emit('trigger')
      setTimeout(() => {
        this.showForm = true
      }, 400)
    },
    handleFormSubmit(resolveStatusId) {
      if (resolveStatusId) {
        this.processing = true
        bulkUpdateApi(this.moduleName, this.selectedIds, {
          statusId: resolveStatusId,
        })
          .then((response) => {
            if (response.failed.length) {
              this.$emit('bulk-update-error', response.failed)
            }
            this.showConfirmModal = false
            this.showForm = false
            this.$emit('done')
          })
          .catch(() => {
            this.processing = false
          })
      } else {
        this.showConfirmModal = false
        this.showForm = false
        this.$emit('done')
      }
    },
    handleInsertTemplate(template) {
      this.text = template.content
      this.attachments = template.fileAttachments
      this.renderCount = this.renderCount + 1
      this.$refs.insertResponseTemplateDrawer.hideDrawer()
    },
    insertKnowledgeContent(data) {
      this.$refs.editorRef.insertHTML(data)
      this.$refs.insertKnowledgeDrawer.hideDrawer()
    },
    handleInsertKnowledge(kb, link) {
      if (link) {
        const url = window.location.href
        let domain = new URL(url)
        const link = `<a href="${domain.origin}/support-portal/knowledge/${kb.id}" target="_blank" >
                  ${domain.origin}/support-portal/knowledge/${kb.id}
                </a><br />`
        this.insertKnowledgeContent(link)
      } else {
        this.insertKnowledgeContent(kb.content)
        this.attachments = kb.fileAttachments
      }
      this.renderCount = this.renderCount + 1
    },
    handleCancelBulkUpdateStatus() {
      this.showConfirmModal = false
      this.handleFormSubmit()
    },
    handleSolutionUpdate() {
      if (
        this.text &&
        this.myAllowedModules.indexOf('request.request_can_manage_solution') >=
          0
      ) {
        const data = {
          solution: this.text,
          solutionFileAttachments: this.attachments,
        }
        const apiFn =
          this.moduleName === this.$constants.REQUEST
            ? bulkUpdateRequestSolutionApi
            : bulkUpdateProblemSolutionApi

        apiFn(this.selectedIds, data)
          .then((response) => {
            if (response.failed.length) {
              this.$emit('bulk-update-error', response.failed)
            }
          })
          .then(() => (this.showConfirmModal = true))
      } else {
        if (
          this.myAllowedModules.indexOf(
            'request.request_can_manage_solution'
          ) === -1
        ) {
          errorToast(this.$tc('do_not_have_manage_solution_permission'))
        }
      }
    },
  },
}
</script>
